import { inject, Injectable, signal } from '@angular/core'

import { forkJoin, map, Observable, of, shareReplay, tap } from 'rxjs'

import { pickLabel } from '@yoshu-lib/utils/pick-label'

import { TranslateService } from '@ngx-translate/core'
import { LocaleService } from '@yoshu-lib/services/locale.service'

import { EntitiesService } from './entities.service'
import { rootTypeSlug } from './entity-type-root'
import { EntityType, RelationshipType } from './entity-type.model'

export interface EntityTypeNode {
  type: EntityType
  parentSlug: string | null
  childSlugs: string[]
}

@Injectable({ providedIn: 'root' })
export class DataModelStore {
  private readonly entitiesService = inject(EntitiesService)
  private readonly locale = inject(LocaleService)
  private readonly translate = inject(TranslateService)

  private readonly _entityTypes = signal<EntityType[]>([])
  private readonly _allEntityTypes = signal<EntityType[]>([])
  private readonly _relationships = signal<RelationshipType[]>([])
  private readonly _hierarchy = signal<Map<string, EntityTypeNode>>(new Map())
  private readonly _loaded = signal(false)
  private readonly _loading = signal(false)
  private _inflight$: Observable<void> | null = null

  readonly entityTypes = this._entityTypes.asReadonly()
  /**
   * Every type the API returned, `is_system` ones included and unsorted.
   *
   * `entityTypes` is the list the record-editing surfaces want, with system
   * types dropped. The configuration inspector is about the configuration
   * itself, where hiding a type would hide what a key user came to check, so it
   * reads this one. Both come from the single load, so neither costs a request.
   */
  readonly allTypesIncludingSystem = this._allEntityTypes.asReadonly()
  readonly relationships = this._relationships.asReadonly()
  readonly hierarchy = this._hierarchy.asReadonly()
  readonly loaded = this._loaded.asReadonly()
  readonly loading = this._loading.asReadonly()

  load(): Observable<void> {
    if (this._loaded()) return of(undefined)
    if (this._inflight$) return this._inflight$

    this._loading.set(true)

    this._inflight$ = forkJoin([
      this.entitiesService.getAllEntityTypes(),
      this.entitiesService.getAllRelationshipTypes()
    ]).pipe(
      map(([allTypes, rels]) => {
        // Keep `attachment` despite `is_system: true` : its `metadata.categories`
        // drives the upload-affordance gate in entity-detail. Other system types
        // remain filtered out of the admin surface.
        const types = allTypes.filter((t) => !t.is_system || t.slug === 'attachment')
        const uniqueAll = uniqueBySlug(allTypes)
        const uniqueTypes = uniqueBySlug(types)

        this.applySnapshot(uniqueTypes, uniqueBySlug(rels), uniqueAll)
        this._loading.set(false)
        this._inflight$ = null
      }),
      tap({
        error: () => {
          this._loading.set(false)
          this._inflight$ = null
        }
      }),
      shareReplay(1)
    )

    return this._inflight$
  }

  private applySnapshot(types: EntityType[], rels: RelationshipType[], allTypes: EntityType[]) {
    this._entityTypes.set(types)
    this._allEntityTypes.set(allTypes)
    this._relationships.set(rels)
    this._hierarchy.set(this.buildHierarchy(types, rels))
    this._loaded.set(true)
  }

  getRootTypes(): EntityType[] {
    const hierarchy = this._hierarchy()
    return Array.from(hierarchy.values())
      .filter((node) => node.parentSlug === null)
      .map((node) => node.type)
  }

  getChildTypes(parentSlug: string): EntityType[] {
    const node = this._hierarchy().get(parentSlug)
    if (!node) return []
    return node.childSlugs
      .map((slug) => this._hierarchy().get(slug)?.type)
      .filter((t): t is EntityType => !!t)
  }

  getType(slug: string): EntityType | undefined {
    return this._hierarchy().get(slug)?.type
  }

  getNode(slug: string): EntityTypeNode | undefined {
    return this._hierarchy().get(slug)
  }

  isLeaf(slug: string): boolean {
    return this._hierarchy().get(slug)?.type.is_leaf ?? false
  }

  /** See `rootTypeSlug`: the top of this type's branch, null when ambiguous. */
  getRootTypeSlug(slug: string): string | null {
    return rootTypeSlug(this._relationships(), slug)
  }

  getFieldLabel(type: EntityType, fieldName: string): string {
    if (fieldName === 'name') return this.translate.instant('admin.entity_detail.name_column')
    if (fieldName === 'created_at') return this.translate.instant('admin.entity_detail.created_at')
    if (fieldName === 'updated_at') return this.translate.instant('admin.entity_detail.updated_at')
    if (fieldName === 'occurrences')
      return this.translate.instant('admin.entity_detail.occurrences')
    if (fieldName === 'setpoint_count')
      return this.translate.instant('admin.entity_detail.setpoint_count')
    if (fieldName === 'sku_count') return this.translate.instant('admin.entity_detail.sku_count')
    const field = type.field_schema.find((f) => f.name === fieldName)
    return pickLabel(field?.ui?.label, this.locale.currentLang()) || fieldName
  }

  private buildHierarchy(
    types: EntityType[],
    rels: RelationshipType[]
  ): Map<string, EntityTypeNode> {
    const map = new Map<string, EntityTypeNode>()

    for (const type of types) {
      map.set(type.slug, {
        type,
        parentSlug: null,
        childSlugs: []
      })
    }

    for (const rel of rels) {
      if (!rel.is_hierarchical) continue
      const parentNode = map.get(rel.source_type_slug)
      const childNode = map.get(rel.target_type_slug)

      if (parentNode && childNode) {
        parentNode.childSlugs.push(rel.target_type_slug)
        childNode.parentSlug = rel.source_type_slug
      }
    }

    return map
  }
}

function uniqueBySlug<T extends { slug: string }>(items: T[]): T[] {
  const seen = new Set<string>()
  const unique: T[] = []
  for (const item of items) {
    if (seen.has(item.slug)) continue
    seen.add(item.slug)
    unique.push(item)
  }
  return unique
}
